"use client"

import { QueryClient, QueryClientProvider, isServer } from "@tanstack/react-query"
import { ProgressProvider } from "@bprogress/next/app"
import dayjs from "dayjs"
import timezone from "dayjs/plugin/timezone"
import utc from "dayjs/plugin/utc"
import dynamic from "next/dynamic"
import React from "react"

import type { TLoginUserData } from "@/schemas/user.schema"
import { AppStoreProvider } from "@/stores/appStore"
import { ModalsProvider } from "@mantine/modals"

import { ClientRuntimeEffects } from "@/app/(normal)/ClientRuntimeEffects"
import { FullStoryProvider } from "@/hooks/useFullStory"
import { createQueryClient } from "@/lib/query-defaults"

dayjs.extend(utc)
dayjs.extend(timezone)

const FeatureBase = dynamic(() => import("@/components/generic/FeatureBase").then((mod) => mod.FeatureBase), {
  ssr: false,
})

const SnowEffect = dynamic(() => import("@/components/generic/SnowEffect").then((mod) => mod.SnowEffect), {
  ssr: false,
})

let browserQueryClient: QueryClient | undefined = undefined

export function getQueryClient() {
  if (isServer) {
    return createQueryClient()
  } else {
    if (!browserQueryClient) browserQueryClient = createQueryClient()
    return browserQueryClient
  }
}
interface IClientProviders extends React.PropsWithChildren {
  userData?: TLoginUserData
  intercomUserHash?: string
  featurebaseUserHash?: string
  featurebaseCompanyHash?: string
  ssr?: {
    webphoneExpanded?: boolean
  }
}

export function ClientProviders({ children, userData, intercomUserHash, featurebaseCompanyHash, featurebaseUserHash, ssr }: IClientProviders) {
  const hasAuthenticatedUser = Boolean(userData?.user?.id && userData.user.id > 0 && userData?.account?.status && userData.account.status !== "LOGGED_OUT")

  return (
    <FullStoryProvider enabled={hasAuthenticatedUser}>
      <AppStoreProvider userData={userData} webphoneExpanded={ssr?.webphoneExpanded}>
        <ProgressProvider height="4px" color="#5EB5F3" options={{ showSpinner: false }} shallowRouting>
          <SubClientProviders userData={userData} intercomUserHash={intercomUserHash} featurebaseCompanyHash={featurebaseCompanyHash} featurebaseUserHash={featurebaseUserHash}>
            {children}
          </SubClientProviders>
        </ProgressProvider>
      </AppStoreProvider>
    </FullStoryProvider>
  )
}

function SubClientProviders({ children, userData, intercomUserHash, featurebaseCompanyHash, featurebaseUserHash }: IClientProviders) {
  const queryClient = getQueryClient()

  return (
    <QueryClientProvider client={queryClient}>
      <ModalsProvider>
        <ClientRuntimeEffects userData={userData} intercomUserHash={intercomUserHash} />
        {children}
        <FeatureBase companyHash={featurebaseCompanyHash} userHash={featurebaseUserHash} userData={userData} />
        <SnowEffect />
      </ModalsProvider>
    </QueryClientProvider>
  )
}
